import { DowngradeSubscriptionModal } from "@components/_subscription/basic-plan-card/DowngradeSubscriptionModal"
import { SubscriptionType } from "@components/_subscription/helpers/constants"
import { Button } from "@components/ui/button"
import { Heading } from "@components/ui/typograhpy/Heading"
import { Text } from "@components/ui/typograhpy/Text"
import { CheckoutPlan } from "@generated"
import { ActivePlanType } from "@lib/get-active-plan-type"
import { getGraphqlClient } from "@lib/graphql-client"
import { useCurrency } from "@lib/hooks/use-currency"
import { useGetSubscriptionDetails } from "@lib/hooks/use-get-subscription-details"
import { useMutation } from "@tanstack/react-query"
import Link from "next/link"
import React, { FC } from "react"
import { PlanCardUsp } from "../PlanCardUsp"

const { sdk } = getGraphqlClient()

type BasicPlanCardPropsVariantStatic = {
  variant: "static"
  activePlanType?: never
}
type BasicPlanCardPropsVariantDynamic = {
  variant: "dynamic"
  activePlanType: ActivePlanType
}

type BasicPlanCardProps =
  | BasicPlanCardPropsVariantStatic
  | BasicPlanCardPropsVariantDynamic

export const BasicPlanCard: FC<BasicPlanCardProps> = ({
  variant,
  activePlanType,
}) => {
  const { currency } = useCurrency()
  const { data: subscriptionDetails } = useGetSubscriptionDetails()
  const subscriptionDetail = subscriptionDetails[SubscriptionType.BASIC]
  const createCheckoutSessionMutation = useMutation({
    mutationFn: async () =>
      await sdk.CreateCheckoutSession({
        input: {
          currency,
          cancelUrl: `${window.location.origin}/app?subscription=error`,
          successUrl: `${
            window.location.origin
          }/app?subscription=success&subscribed_plan=Basic&purchase_time=${new Date().toISOString()}`,
          plan: CheckoutPlan["BasicPlan_2023V1"],
          downgradeReason: null,
        },
      }),
  })

  return (
    <div className="bg-border-disabled relative rounded-md px-5 py-6 md:px-6">
      <div className="mb-6">
        <Heading variant="h6" as="h2">
          Basic
        </Heading>
        <Text variant="regular" className="text-shade-400">
          {subscriptionDetail.initialCredit} credits /month
        </Text>
      </div>
      <div className="mb-6">
        <Heading variant="h4" as="h3">
          {subscriptionDetail.monthlyPrice}
        </Heading>
        <Text variant="regular" className="font-semibold">
          /month
        </Text>
      </div>
      <div className="mb-8">{renderButton()}</div>
      <ul>
        {subscriptionDetail.usps.map((usp, index) => (
          <PlanCardUsp variant="default" key={index}>
            {usp}
          </PlanCardUsp>
        ))}
      </ul>
    </div>
  )
  async function handleClickSubscribe() {
    const result = await createCheckoutSessionMutation.mutateAsync()
    if (result && result.createCheckoutSession.url)
      location.href = result.createCheckoutSession.url
  }

  function renderButton() {
    if (variant === "static") {
      return (
        <Link
          href="/signup/create-account?redirect_to_stripe=true&subscribed_plan=Basic"
          passHref
          legacyBehavior
        >
          <Button
            className="text-shade-800"
            variant="white"
            width="fill"
            as="link"
          >
            Subscribe
          </Button>
        </Link>
      )
    }
    if (activePlanType === "FREE") {
      return (
        <Button
          className="text-shade-800"
          variant="white"
          width="fill"
          onClick={handleClickSubscribe}
          status="idle"
          leftIcon={undefined}
        >
          Upgrade plan
        </Button>
      )
    }

    if (activePlanType === "BASIC") {
      return (
        <Button
          className="text-shade-800"
          variant="white"
          width="fill"
          onClick={handleClickSubscribe}
          status="disabled"
          leftIcon="check"
        >
          Current plan
        </Button>
      )
    }

    if (activePlanType === "BUSINESS") {
      return <DowngradeSubscriptionModal status="idle" />
    }

    return (
      <Button
        className="text-shade-800"
        variant="white"
        width="fill"
        status="disabled"
      >
        Enterprise plan is active
      </Button>
    )
  }
}
